import { useCallback, useContext, useEffect, useState } from "react"
import Controls from "../Controls/controls"
import { IJoinArgs, Layout, MediaType } from "../../definitions"
import { IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng"
import AgoraRegularManager from "../Agora/AgoraRegularManager"
import { AgoraTrackType } from "../Agora/AgoraRtcCustomTypes"
import MultiVideos from "../Videos/multi-videos"
import { getCameraConfig, getRandomInt } from "../Utils/app"
import AdditionalRegularDevice from "../AdditionalDevice/AdditionalRegularDevice"
import AvatarImage from "../Avatar/Avatar"
import DevicesModal from "../devices-modal/devices-modal"
import { CameraContext } from "../Contexts/cameraContext"
import { AgoraManagerType } from "../Agora/AgoraManager"
import AdditionalAudioDevice from "../AdditionalDevice/AdditionalAudioDevice"
import ScreenShare from "../ScreenShare/ScreenShare"

// TODO: Replace this function with a non specific app function
import { notify } from "@functions/notification"

interface IVideocallProps {
    onLeaveChannel?: (...args: unknown[]) => void,
    onUserJoined?: () => void,
    onUserUnJoined?: () => void,
    username: string,
    incomingUsername: string,
    channelName: string,
    appId: string,
    token: string,
    initialMediaArgs: IJoinArgs
    children:JSX.Element
 
}
export type videoCallSizeModes = "VideoCall__Minimized" | "VideoCall__MinimizedAction" | "VideoCall__FullScreen"

interface IVideocallMediaFlags
{
    showVideo: boolean,
    showAudio: boolean,
    showScreen: boolean
}

interface IAdditionalDevice
{
    managerId: number,
    device: MediaDeviceInfo
}

const Videocall = (props: IVideocallProps) => {
    const { channelName, username } = props
    const agoraCredentials = {
        appId: props.appId,
        channel: props.channelName,
        token: props.token,
        uid: props.username
    }

    const { camera } = useContext(CameraContext)

    const [ manager, setManager ] = useState<AgoraRegularManager>()

    const [userList, setUserLists] = useState<string[]>([])

    // Users connected to the videocall
    const [users, setUsers] = useState<IAgoraRTCRemoteUser[]>([])

    // Check if the videocall has started
    const [started, setStarted] = useState<boolean>(false)
    const [ endCall, setEndCall ] = useState<boolean>(false)

    const [ additionalRegularDevices, setAdditionalRegularDevices ] = useState<IAdditionalDevice[]>([])
    const [ additionalAudioDevices, setAdditionalAudioDevices ] = useState<MediaDeviceInfo[]>([])

    const [mediaFlags, setMediaFlags] = useState<IVideocallMediaFlags>({
        showVideo: false,
        showAudio: false, 
        showScreen: false
    })

    const [videoCallSizeMode, setVideoCallSizeMode] = useState<videoCallSizeModes>("VideoCall__Minimized")
    const [expanded, setIsExpanded] = useState<boolean>(false)

    // Selected layout by the user. Regular (Grid) layout will be the default
    const [selectedLayout, setSelectedLayout] = useState<Layout>(Layout.SPOTLIGHT)

    // Additional devices
    const [openModalMediaDevices, setOpenModalMediaDevices] = useState(false)

    //Used to store aditional devices selected by users
    const [selectedMediaDevices, setSelectedMediaDevices] = useState<MediaDeviceInfo[]>([])
    const [devicesBroadcasting, setDevicesBroadcasting] = useState<MediaDeviceInfo[]>([])

    /**
     * Start regular client
     */
    useEffect(() => {
        if(!manager)
        {
            const mng = new AgoraRegularManager(agoraCredentials)
    
            mng.initialize(getCameraConfig()).then(() => {
                console.log("Client initialized")
                setManager(mng)
            })
        }

        return () => {
            if(!manager) return
            // TODO: Remove after testing. Destroys on leave channel
            // manager.destroy()
            leaveChannel()
            setManager(undefined)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manager])

    /**
     * Main videocall handler
     * 
     * It executes after the conditions are fulfilled
     */
    useEffect(() => {
        if(!manager?.client) return
        if(!manager?.tracks) return
        if(!channelName) return
        if(!manager) return
        if(!props.token) return

        // function to initialise the SDK
        const init = async(name: string, mng: AgoraRegularManager) => {
            if(!mng?.client) return undefined
            console.log("init", name)

            /**
             * Subscribe to new user on videocall stream signal. 
             * Add the newly subscribed user to the user list
             */
            mng.client.on("user-published", async (user, mediaType) => {
                await mng.client?.subscribe(user, mediaType)
                if (mediaType === "video") {
                    setUsers((prevUsers) => {
                        return [...prevUsers, user]
                    })
                }
                if (mediaType === "audio") {
                    user.audioTrack?.play()
                }
            })

            // Remove user from user list
            mng.client.on("user-unpublished", (user, type) => {
                console.debug("user-unpublished", user)
                if (type === "audio") {
                    user.audioTrack?.stop()
                }
                if (type === "video") {
                    user.videoTrack?.stop()
                    setUsers((prevUsers) => {
                        return prevUsers.filter(
                            (User) => User.uid !== user.uid
                        )
                    })
                }
            })

            // Event triggered when a new user joins to videocall
            mng.client.on("user-joined", user => {
                console.debug("User joined", user)
                if(user.uid.toString() !== username){
                    setUserLists((prevUsers) => {
                        return [...prevUsers, user.uid.toString()]
                    })
                }
                
                if(props.onUserJoined)
                    props.onUserJoined()
            })

            // Event triggered when an user left the call
            mng.client.on("user-left", (user) => {
                console.debug("Leaving room...", user)
                if(user.uid.toString() === props.incomingUsername || user.uid.toString() === username)
                    leaveChannel()
                setUsers((prevUsers) => {
                    return prevUsers.filter((User) => User.uid !== user.uid)
                })
                setUserLists((prevUsers) => {
                    return prevUsers.filter((User) => User !== user.uid)
                })

                if(props.onUserUnJoined)
                    props.onUserUnJoined()
            })

            // Join the client to a room session.
            // This will not start the audio/video track sharing
            await mng.client.join(props.appId, name, props.token, mng.uid)
            if(mng.tracks !== undefined)
            {
                const videoTrack = mng.tracks[AgoraTrackType.VIDEO]
                if(videoTrack)
                {
                    await mng.client.publish([
                        mng.tracks[AgoraTrackType.AUDIO],
                        videoTrack
                    ])
                    console.debug("All tracks published")
                }
                else
                {
                    await mng.client.publish([
                        mng.tracks[AgoraTrackType.AUDIO]
                    ])
                    console.debug("Only audio track published")
                }
            }

            setStarted(true)
        }

        if(!started) 
        {
            // Start video components
            setMediaFlags(current => current = {
                ...current,
                showVideo: props.initialMediaArgs.video
            })

            // Add values from lobby
            manager.tracks[AgoraTrackType.AUDIO].setMuted(!props.initialMediaArgs.audio)
            manager.tracks[AgoraTrackType.VIDEO]?.setMuted(!props.initialMediaArgs.video)

            // Start videocall
            init(channelName, manager)
                .catch(err => handleError(err))
            setStarted(true)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelName, manager, started, users, userList])

    /**
     * Listens for user media devices selection
     */
    useEffect(() => {
        // Filter the devices that are not currently broadcasting and is not the camera in use
        const newDevices = selectedMediaDevices
            .filter(dev => !devicesBroadcasting.some(db => db.deviceId === dev.deviceId) 
                && camera?.deviceId !== dev.deviceId)

        // Add devices to the extra audio/regular(video) arrays
        newDevices.forEach(dev => {
            switch(dev.kind)
            {
                case "videoinput":
                    addAdditionalRegularDevice(dev)
                    break
                case "audioinput":
                    addAdditionalAudioDevice(dev)
                    break
                default:
                    console.warn("Device unsupported - videocall additional device", dev)
            }
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMediaDevices])

    useEffect(() => {
        if(!endCall) return
        if(additionalRegularDevices.length > 0 || additionalAudioDevices.length > 0 ||
            selectedMediaDevices.length > 0 || devicesBroadcasting.length > 0) return

        setTimeout(() => {
            console.debug("Ending call")
            leaveChannel()
        }, 600)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endCall, additionalRegularDevices, additionalAudioDevices, selectedMediaDevices, devicesBroadcasting])

    /**
     * Handles error. Need work
     * 
     * TODO: Finish this function to validate errors properly
     * @param err 
     */
    const handleError = (err: unknown) => {
        console.error("Agora error", err)
    }

    /**
     * Mute/unmute Video or audio tracks
     * @param type 
     * @param enable 
     * @returns 
     */
    const mute = async(type: MediaType, enable: boolean) => {
        if(!manager?.tracks) return

        const ttype = type === "audio" ? AgoraTrackType.AUDIO : AgoraTrackType.VIDEO  
        if(type === "audio"){ 
            await manager.tracks[ttype]?.setEnabled(true)
            await manager.tracks[ttype]?.setMuted(!enable)
        }
        else if(type === "video" && manager.tracks.length > 1)
        {
            await manager.tracks[ttype]?.setMuted(false)
            await manager.tracks[ttype]?.setEnabled(enable)
            setMediaFlags(current => current = {
                ...current,
                showVideo: enable
            })
        }
    }

    /**
     * Leave videocall
     */
    const leaveChannel = async() => {
        await manager?.destroy()
        setStarted(false)

        if(props.onLeaveChannel)
            props.onLeaveChannel()
    }

    const leaveChannelHandler = () => {
        console.debug("Cleaning states")
        setAdditionalRegularDevices([])
        setAdditionalAudioDevices([])
        setSelectedMediaDevices([])
        setDevicesBroadcasting([])
        setEndCall(true)
    }

    /**
     * @abstract To change videocall size between fullscreen, minimized as small and minimizedaction only arrow in left
     */
    const handleVideoSizeMode = (isExpand = false) => {
        switch (videoCallSizeMode)
        {
            case "VideoCall__MinimizedAction":
                setVideoCallSizeMode("VideoCall__Minimized")
                break
            case "VideoCall__FullScreen":
                setVideoCallSizeMode("VideoCall__Minimized")
                setIsExpanded(false)
                break
            case "VideoCall__Minimized":
                isExpand ? setVideoCallSizeMode("VideoCall__FullScreen") : setVideoCallSizeMode("VideoCall__MinimizedAction")
                setIsExpanded(isExpand)
                break
            default:
                break
        }
    }

    /**
     * @abstract Function to add a new device to ou array that containts current extra media devices
     */
    const addAdditionalRegularDevice = (camera: MediaDeviceInfo) => {
        let rnd: number
        let loops = 0

        // Get random number. Verify that is does not repeat
        do
        {
            rnd = getRandomInt()
            loops++
        }
        // eslint-disable-next-line no-loop-func
        while(additionalRegularDevices.some(dev => dev.managerId === rnd) && loops < 10)

        const additionalDevice = {
            managerId: rnd,
            device: camera
        }

        setAdditionalRegularDevices(c => c = [...c, additionalDevice])
    }

    /**
     * @abstract Function to add a new device to ou array that containts current extra media audio devices
     */
    const addAdditionalAudioDevice = (device: MediaDeviceInfo) => {
        if(additionalAudioDevices.some(dev => dev.deviceId === device.deviceId))
            return
        setAdditionalAudioDevices(c => c = [...c, device])
    }

    /**
     * Handles error. Need work
     * 
     * TODO: Finish this function to validate errors properly
     * @param err 
     */
    const onAdditionalDeviceErrorHandler = (type: AgoraManagerType, error: unknown, sourceId?: number | string) => {
        if(type === "screen")
        {
            setMediaFlags(current => {
                return {
                    ...current,
                    showScreen: false
                }
            })

            return
        }

        if(!sourceId)
        {
            console.error("onAdditionalDeviceErrorHandler", error)
            return
        }

        if(typeof error === "string" && error === "TRACK_ENDED")
        {
            if(type === "regular")
            {
                const device = getDevice("regular", sourceId)
                    if(!device) return
                onRemoveMediaDevices(device)

                // Remove additional camera device alert
                // Timeout is necesary to handle async process
                setTimeout(() => {
                    const msg = "Additional device has been disconnected"
                    // alert(msg)
                    notify({message: msg, messageType: "info"})
                }, 500)
            }
            else if(type === "audio")
            {
                const device = getDevice("audio", sourceId)
                    if(!device) return
                onRemoveMediaDevices(device)

                // Remove additional mic device alert. 
                // Timeout is necesary to handle async process
                setTimeout(() => {
                    const msg = "Additional audio device has been disconnected"
                    // alert(msg)
                    notify({message: msg, messageType: "info"})
                }, 500)
            }
            else
            {
                console.error("onAdditionalDeviceErrorHandler - TRACK_ENDED", error)
            }
        }
        else
        {
            console.error(`onAdditionalDeviceErrorHandler - Source ${sourceId}`, error)
        }
    }

    /**
     * Control which devices are casting signals (client/tracks).
     * 
     * Uses `devicesBroadcasting` array state to keep control
     * @param published 
     * @param type 
     * @param sourceId 
     * @returns 
     */
    const onStartAdditionalDevHandler = (published: boolean, type: AgoraManagerType, sourceId: number | string) => {
        console.debug("Additional device start", sourceId)
        if(published)
        {
            const device = getDevice(type, sourceId)
            if(!device) return

            setDevicesBroadcasting((prevDevicesBroadcasting) => {
                return [...prevDevicesBroadcasting, device as MediaDeviceInfo]
            })
        }
    }
    
    /**
     * Remove a device from `devicesBroadcasting` array state if
     * the signal has stoped
     * 
     * @param published 
     * @param type 
     * @param sourceId 
     * @returns 
     */
    const onStopAdditionalDevHandler = (type: AgoraManagerType, sourceId: number | string) => {
        console.debug("Additional device stop", sourceId)

        const device = getDevice(type, sourceId)
        if(!device) return

        const filtered = devicesBroadcasting.filter(dev => dev.deviceId !== (device as MediaDeviceInfo).deviceId)
        setDevicesBroadcasting(filtered)
    }

    /**
     * Find additional device according to their type
     * @param type 
     * @param sourceId `deviceId` or `managerId` 
     */
    const getDevice = useCallback((type: AgoraManagerType, sourceId: number | string) => {
        let device: MediaDeviceInfo | undefined
        if(type === "regular")
        {
            const regularDevice = additionalRegularDevices.find(eachDevice => eachDevice.managerId === sourceId)
            device = regularDevice?.device
        }
        else if(type === "audio")
        {
            device = additionalAudioDevices.find(eachDevice => eachDevice.deviceId === sourceId)
        }

        return device
    }, [additionalRegularDevices, additionalAudioDevices])

    // Modal -> Videocall listado de dispositivos adicionales (MediaDeviceInfo)
    const onAddMediaDevices = (mediaDEvice: MediaDeviceInfo) => {
        setSelectedMediaDevices(prevMediaDevices => prevMediaDevices = [...prevMediaDevices,mediaDEvice])
    }

    /**
     * Used to remove a device from the modal, to execute the function that destroys video in manager you dont need to execute heere
     * When AdditionalRegularDevice destroys its self theres a return that does thata
     */
    const onRemoveMediaDevices = (mediaDevice: MediaDeviceInfo) => {
        const exist = selectedMediaDevices.filter(device => device.deviceId !== mediaDevice.deviceId)
        setSelectedMediaDevices(exist)
        if(mediaDevice.kind === "videoinput"){
            const fromRegularDevices = additionalRegularDevices.filter(regularDevice => regularDevice.device.deviceId !== mediaDevice.deviceId)
            setAdditionalRegularDevices(fromRegularDevices)
        }
        else if(mediaDevice.kind === "audioinput"){
            const fromAudioDevices = additionalAudioDevices.filter(audioDevice => audioDevice.deviceId !== mediaDevice.deviceId)
            setAdditionalAudioDevices(fromAudioDevices)
        }
        else
        {
            console.warn("Remove unsupported for device", mediaDevice)
        }
    }

    return (
        <>
            <DevicesModal 
                openModalDevices={openModalMediaDevices} 
                setOpenModalDevices={(value: boolean) => setOpenModalMediaDevices(value)} 
                selectedMediaDevices={selectedMediaDevices} 
                onAddMediaDevice={onAddMediaDevices} 
                onRemoveMediaDevice={onRemoveMediaDevices} />

            { started && additionalRegularDevices && additionalRegularDevices.map(dev => {
                return (
                    <AdditionalRegularDevice
                        managerId={dev.managerId}
                        source={dev.device}
                        agoraCredentials={agoraCredentials}
                        onStart={onStartAdditionalDevHandler}
                        onError={(error, managerId) => onAdditionalDeviceErrorHandler("regular", error, managerId)}
                        onStopAdditionalDevHandler={onStopAdditionalDevHandler} />
                )
            })}
            
            { started && manager && additionalAudioDevices && additionalAudioDevices.map(dev => {
                return (
                    <AdditionalAudioDevice
                        manager={manager}
                        source={dev}
                        onStart={onStartAdditionalDevHandler}
                        onError={(error, deviceId) => onAdditionalDeviceErrorHandler("audio", error, deviceId)}
                        onStopAdditionalDevHandler={onStopAdditionalDevHandler} />
                )
            })}

            { started && manager && mediaFlags.showScreen &&
                <ScreenShare
                    agoraCredentials={agoraCredentials}
                    onError={(error) => onAdditionalDeviceErrorHandler("screen", error)} />
            }

            <div className="VideoCallContainer">
                <div className="CardLayout-InsideCentered">
                    <div className={videoCallSizeMode} onClick={() => videoCallSizeMode === "VideoCall__MinimizedAction" ?  handleVideoSizeMode() : null}>
                        <em className="fas fa-chevron-right"/>
                        <div className="VideoCall__Video">
                            <div className="VideoCall__TopBar" style={{zIndex: "1"}} >
                                <em className="fas fa-minus" onClick={() => handleVideoSizeMode() }/>
                            </div>

                            {/* <> */}
                            {manager &&
                                <>
                                    {manager.tracks ? 
                                        <MultiVideos
                                            users={users}
                                            tracks={manager.tracks} 
                                            userList={userList} 
                                            start={started} 
                                            showVideo={mediaFlags.showVideo} 
                                            myUsername={props.username} 
                                            layout={selectedLayout}/>
                                        :
                                        <div className="VideoCall__Info">
                                            <div className="VideoCall__Avatar">
                                                <AvatarImage>
                                                    {props.children}
                                                </AvatarImage>
                                            </div>
                                        </div>
                                    }
                                    <Controls
                                        initialValues={props.initialMediaArgs}
                                        isExpanded={expanded}
                                        onExpand={() => handleVideoSizeMode(true)}
                                        onMute={mute}
                                        onLeave={leaveChannelHandler}
                                        audioOnly={manager.audioOnly}
                                        onAddDevice={() => setOpenModalMediaDevices(true)}
                                        canShare={true}
                                        onChangeLayout={value => setSelectedLayout(value)}
                                        screenShared={mediaFlags.showScreen}
                                        onShareScreen={(value: boolean) => setMediaFlags(c => c = {...c, showScreen: value}) }
                                    />
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Videocall
